import { getInitials } from '../lib/initials';
import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getEventById, updateEvent, sendEventNotification } from '../services/events';
import { uploadImage } from '../services/images';
import { getAllUsers } from '../services/users';
import { useAuth } from '../hooks/useAuth';

/** Convert a UTC Date to a local datetime string for <input type="datetime-local"> */
const toLocalDateTimeInput = (date: Date): string => {
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
};

export default function EditEventPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isClanMeeting, setIsClanMeeting] = useState(false);
  const [location, setLocation] = useState('');
  const [startDateTime, setStartDateTime] = useState('');
  const [signupDeadline, setSignupDeadline] = useState('');
  const [signupDeadlineTouched, setSignupDeadlineTouched] = useState(false);
  const [endDateTime, setEndDateTime] = useState('');
  const [endTimeTouched, setEndTimeTouched] = useState(false);
  const [selectedOrganizerIds, setSelectedOrganizerIds] = useState<string[]>([]);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [notifySuccess, setNotifySuccess] = useState<string | null>(null);

  const { data: allUsers = [] } = useQuery({
    queryKey: ['users'],
    queryFn: getAllUsers,
  });

  const { data: event, isLoading } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => getEventById(eventId!),
    enabled: !!eventId,
  });

  useEffect(() => {
    if (!event) {
      return;
    }

    setTitle(event.title);
    setDescription(event.description);
    setIsClanMeeting(event.isClanMeeting);
    setLocation(event.location || '');

    const currentOrganizerIds = event.organizers && event.organizers.length > 0
      ? event.organizers.map((o) => o.user.id)
      : (event.organizerId ? [event.organizerId] : []);
    setSelectedOrganizerIds(currentOrganizerIds);

    const start = new Date(event.startTime);
    setStartDateTime(toLocalDateTimeInput(start));

    const deadline = event.signupDeadline
      ? new Date(event.signupDeadline)
      : new Date(start.getTime() - 7 * 24 * 60 * 60 * 1000);
    setSignupDeadline(toLocalDateTimeInput(deadline));
    setSignupDeadlineTouched(false);

    if (event.endTime) {
      setEndDateTime(toLocalDateTimeInput(new Date(event.endTime)));
      setEndTimeTouched(true);
    } else {
      setEndDateTime('');
      setEndTimeTouched(false);
    }

    setImagePreview(event.imageUrl);
  }, [event]);

  useEffect(() => {
    if (!startDateTime || signupDeadlineTouched) return;
    const start = new Date(startDateTime);
    const def = new Date(start.getTime() - 7 * 24 * 60 * 60 * 1000);
    setSignupDeadline(toLocalDateTimeInput(def));
  }, [startDateTime, signupDeadlineTouched]);

  useEffect(() => {
    if (!startDateTime || endTimeTouched) return;
    const start = new Date(startDateTime);
    const def = new Date(start.getTime() + 3 * 60 * 60 * 1000);
    setEndDateTime(toLocalDateTimeInput(def));
  }, [startDateTime, endTimeTouched]);

  const mutation = useMutation({
    mutationFn: async () => {
      let imageUrl = event?.imageUrl || undefined;

      if (selectedImage) {
        imageUrl = await uploadImage(selectedImage, 'events');
      }

      const start = new Date(startDateTime);
      const defaultDeadline = new Date(start.getTime() - 7 * 24 * 60 * 60 * 1000);
      const deadline = signupDeadline ? new Date(signupDeadline) : defaultDeadline;

      if (deadline >= start) {
        throw new Error('Tilmeldingsfrist skal være før starttidspunkt');
      }

      const end = endDateTime ? new Date(endDateTime) : undefined;
      if (end && end <= start) {
        throw new Error('Sluttidspunkt skal være efter starttidspunkt');
      }

      return updateEvent(eventId!, {
        title: title.trim(),
        description: description.trim(),
        isClanMeeting,
        location: location.trim() || undefined,
        startTime: start.toISOString(),
        signupDeadline: deadline.toISOString(),
        endTime: end?.toISOString(),
        imageUrl,
        organizerIds: selectedOrganizerIds,
      });
    },
    onSuccess: async (updated) => {
      await queryClient.invalidateQueries({ queryKey: ['events'] });
      await queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      navigate(`/events/${updated.id}`);
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message || 'Kunne ikke opdatere begivenhed');
    },
  });

  const notifyMutation = useMutation({
    mutationFn: () => sendEventNotification(eventId!),
    onSuccess: (data) => {
      setNotifySuccess(`Notifikation sendt til ${data.notified} ${data.notified === 1 ? 'bruger' : 'brugere'}`);
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message || 'Kunne ikke sende notifikation');
    },
  });

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isLoading || !event) {
    return (
      <div className="max-w-3xl mx-auto p-4">
        <div className="bg-white rounded-lg shadow p-6 animate-pulse">
          <div className="h-8 bg-gray-300 rounded w-2/3 mb-4"></div>
          <div className="h-4 bg-gray-300 rounded w-full mb-2"></div>
          <div className="h-4 bg-gray-300 rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  const organizerUsers = (event.organizers && event.organizers.length > 0
    ? event.organizers.map((organizer) => organizer.user)
    : (event.organizer ? [event.organizer] : [])).filter(Boolean);
  const canEdit = Boolean(user?.isAdmin)
    || organizerUsers.some((organizer) => organizer.id === user?.id)
    || (event.organizerId != null && event.organizerId === user?.id);

  if (!canEdit) {
    return <Navigate to={`/events/${eventId}`} replace />;
  }

  return (
    <div className="max-w-3xl mx-auto p-4">
      <Link to={`/events/${eventId}`} className="inline-flex items-center text-blue-500 hover:text-blue-700 mb-4">
        ← Tilbage til begivenhed
      </Link>

      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Rediger begivenhed</h1>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-4">
            {error}
          </div>
        )}

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError('');

            if (!title.trim() || !description.trim() || !startDateTime) {
              setError('Udfyld venligst titel, beskrivelse og starttidspunkt');
              return;
            }

            mutation.mutate();
          }}
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titel *</label>
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} required />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Beskrivelse *</label>
            <textarea
              className="input min-h-[120px]"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={5000}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sted</label>
            <input className="input" value={location} onChange={(e) => setLocation(e.target.value)} maxLength={500} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
              <input
                type="checkbox"
                checked={isClanMeeting}
                onChange={(e) => setIsClanMeeting(e.target.checked)}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded"
              />
              Klanmøde
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Starttidspunkt *</label>
            <input type="datetime-local" className="input" value={startDateTime} onChange={(e) => setStartDateTime(e.target.value)} required />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sluttidspunkt</label>
            <input
              type="datetime-local"
              className="input"
              value={endDateTime}
              onChange={(e) => { setEndDateTime(e.target.value); setEndTimeTouched(true); }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tilmeldingsfrist</label>
            <input
              type="datetime-local"
              className="input"
              value={signupDeadline}
              onChange={(e) => { setSignupDeadline(e.target.value); setSignupDeadlineTouched(true); }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Billede</label>
            {imagePreview && (
              <img src={imagePreview} alt={title} className="w-full h-56 object-cover rounded-lg mb-3" />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setSelectedImage(file);
                if (file) {
                  setImagePreview(URL.createObjectURL(file));
                }
              }}
            />
          </div>

          {/* Organizers */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Arrangører *</label>
            <div className="border border-gray-300 rounded-lg max-h-48 overflow-y-auto divide-y divide-gray-100">
              {allUsers.map((u) => (
                <label key={u.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedOrganizerIds.includes(u.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedOrganizerIds((prev) => [...prev, u.id]);
                      } else {
                        setSelectedOrganizerIds((prev) => prev.filter((id) => id !== u.id));
                      }
                    }}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  {u.profileImageUrl ? (
                    <img src={u.profileImageUrl} alt={u.name} className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center text-xs font-semibold text-primary-700">
                      {getInitials(u.name)}
                    </div>
                  )}
                  <span className="text-sm text-gray-800">{u.name}</span>
                </label>
              ))}
            </div>
            {selectedOrganizerIds.length === 0 && (
              <p className="text-xs text-red-500 mt-1">Vælg mindst én arrangør</p>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={mutation.isPending || selectedOrganizerIds.length === 0} className="btn-primary flex-1 disabled:opacity-50">
              {mutation.isPending ? 'Gemmer...' : 'Gem ændringer'}
            </button>
            <Link to={`/events/${eventId}`} className="btn-secondary">
              Annuller
            </Link>
          </div>
        </form>

        <div className="mt-6 rounded-lg p-5" style={{ background: '#eaf4ff', border: '1.5px solid #93c5fd' }}>
          <div className="flex items-start gap-3">
            <span className="text-2xl leading-none mt-0.5">🔔</span>
            <div className="flex-1">
              <h2 className="text-base font-semibold mb-1" style={{ color: '#1e3a5f' }}>Send notifikation</h2>
              <p className="text-sm mb-4" style={{ color: '#1e40af' }}>
                Send en notifikation til alle brugere om denne begivenhed. Brugere der har slået event-notifikationer fra, modtager ikke beskeden.
              </p>
              {notifySuccess && (
                <div className="rounded-lg p-3 mb-4 text-sm" style={{ background: '#f0fdf4', border: '1px solid #86efac', color: '#166534' }}>
                  ✓ {notifySuccess}
                </div>
              )}
              <button
                type="button"
                disabled={notifyMutation.isPending}
                className="btn-primary disabled:opacity-50"
                style={{ padding: '0.6rem 1.4rem', fontSize: '1rem', borderRadius: '8px', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                onClick={() => {
                  setNotifySuccess(null);
                  setError('');
                  notifyMutation.mutate();
                }}
              >
                🔔 {notifyMutation.isPending ? 'Sender...' : 'Send notifikation'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
